import { computed, ref } from 'vue';
import { createGlobalState } from '@vueuse/core';
import {
    CronNotificationApi,
    type CronNotificationRun,
} from '@eureka/services/src/apis/cron-notification.api';

const POLL_INTERVAL_MS = 60_000;

export const useCronNotifications = createGlobalState(() => {
    const runs = ref<CronNotificationRun[]>([]);
    const unreadCount = ref(0);
    const unreadSessionIds = ref<string[]>([]);
    const dismissedHomeSessionIds = ref<string[] | null>(null);
    let baseURL = '';
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let stateRevision = 0;
    let refreshPromise: Promise<void> | undefined;

    const latestRun = computed(() => runs.value[0]);
    const homeNotificationRun = computed(() => dismissedHomeSessionIds.value ? undefined : latestRun.value);

    function configure(serviceBaseURL: string) {
        baseURL = serviceBaseURL;
    }

    function clear() {
        stateRevision++;
        runs.value = [];
        unreadCount.value = 0;
        unreadSessionIds.value = [];
        dismissedHomeSessionIds.value = null;
    }

    function refresh(): Promise<void> {
        if (!baseURL || refreshPromise) return refreshPromise || Promise.resolve();
        const revisionAtStart = stateRevision;
        refreshPromise = CronNotificationApi.getUnreadSummary(baseURL)
            .then(summary => {
                if (revisionAtStart !== stateRevision) return;
                if (dismissedHomeSessionIds.value &&
                    summary.session_ids.some(id => !dismissedHomeSessionIds.value?.includes(id))) {
                    dismissedHomeSessionIds.value = null;
                }
                runs.value = summary.latest_run ? [summary.latest_run] : [];
                unreadCount.value = summary.total;
                unreadSessionIds.value = summary.session_ids;
                console.debug('[Cron] Unread notifications refreshed:', {
                    total: summary.total,
                    sessionCount: summary.session_ids.length,
                });
            })
            .catch(error => {
                console.error('[Cron] Failed to load unread notifications:', error);
            })
            .finally(() => {
                refreshPromise = undefined;
            });
        return refreshPromise;
    }

    async function confirmSessionRead(sessionId: string) {
        stateRevision++;
        const wasUnread = unreadSessionIds.value.includes(sessionId);
        unreadSessionIds.value = unreadSessionIds.value.filter(id => id !== sessionId);
        runs.value = runs.value.filter(run => run.session_id !== sessionId);
        if (wasUnread) unreadCount.value = Math.max(0, unreadCount.value - 1);
        if (refreshPromise) await refreshPromise;
        await refresh();
    }

    function dismissHomeNotification() {
        dismissedHomeSessionIds.value = [...unreadSessionIds.value];
    }

    function refreshWhenVisible() {
        if (document.visibilityState === 'visible') refresh();
    }

    function startPolling() {
        if (pollTimer) return;
        pollTimer = setInterval(refreshWhenVisible, POLL_INTERVAL_MS);
        document.addEventListener('visibilitychange', refreshWhenVisible);
    }

    function stopPolling() {
        if (pollTimer) clearInterval(pollTimer);
        pollTimer = undefined;
        document.removeEventListener('visibilitychange', refreshWhenVisible);
    }

    return {
        unreadCount,
        unreadSessionIds,
        latestRun,
        homeNotificationRun,
        configure,
        clear,
        refresh,
        startPolling,
        stopPolling,
        confirmSessionRead,
        dismissHomeNotification,
    };
});
